import { useEffect, useRef, useState } from 'react';

import {
  fetchPostApplication,
  fetchPostPartnerSignupVisit,
} from '@iwoca/lapi-client/edge';

import {
  useGetApplicationQuery,
  useGetContext,
  useGetProfile,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../api/lendingApi/lendingApiQueries';
import { normaliseStepData } from '../../components/helpers/stateNormalisation';
import { getInitialApplicationBodyUK } from '../../helpers/getApplicationBody';
import { getLatestStepUK } from '../../helpers/getLatestStep/getLatestStepUK';
import { TGetStateResponse } from '../../types';
import { useSignup } from '../useSignup';
import { useSignupParams } from '../useSignupParams';
import { useSignupRouterUK } from '../useSignupRouter/useSignupRouterUK';
import { useSplitTestContext } from '../useSplitTestContext';

export const useAutoRoutingUK = () => {
  const hasRedirectedRef = useRef(false);

  const { isProfileErrored } = useGetProfile();
  const { context, contextStatus } = useGetContext();
  const { state } = useGetStateQuery<TGetStateResponse>();

  const { application, applicationStatus, refetchApplication } =
    useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery((data) =>
    normaliseStepData(data),
  );

  const { setIsLoading } = useSignup();

  const { affiliateTag, fromCampaign, isDirectCustomer, isPartnerCustomer } =
    useSignupParams();

  const { currentStep, navigateSignup, navigateToAccountPages } =
    useSignupRouterUK();

  const { hasAIQuestionSignupEnabled } = useSplitTestContext();

  const [hasPostedPartnerSignupVisit, setHasPostedPartnerSignupVisit] =
    useState(false);

  const updateAndRefetchApplication = async (stateKey: string) => {
    setIsLoading(true);

    await fetchPostApplication({
      stateKey,
      body: getInitialApplicationBodyUK(affiliateTag),
    });

    await refetchApplication();
    setIsLoading(false);
  };

  // Stop future auto redirects if profile errors
  useEffect(() => {
    if (isProfileErrored) {
      hasRedirectedRef.current = true;
    }
  }, [isProfileErrored]);

  useEffect(() => {
    if (
      normalisedState &&
      typeof context?.state_key === 'string' &&
      applicationStatus === 'success' &&
      !hasRedirectedRef.current
    ) {
      const isNewOrEmpty = currentStep === 'new' || currentStep === '';
      if (
        isNewOrEmpty &&
        !isDirectCustomer &&
        !isProfileErrored &&
        !hasPostedPartnerSignupVisit
      ) {
        fetchPostPartnerSignupVisit({
          body: {
            data: {
              affiliate_tag: affiliateTag,
              from_campaign: fromCampaign || undefined,
            },
          },
        });
        setHasPostedPartnerSignupVisit(true);
      }

      if (application?.status === 'deferred') {
        void updateAndRefetchApplication(context.state_key);
        return;
      }

      if (
        (application?.status === 'offered' && application.is_closed) ||
        normalisedState.ui?.max_reached_step === 'myAccount'
      ) {
        void navigateToAccountPages(context.state_key);
        return;
      }

      hasRedirectedRef.current = true;

      const activeStep = normalisedState.ui?.activePartnerStep;
      const newStep = getLatestStepUK({
        newState: state,
        newApplication: application,
        activeStep,
        partnerCustomer: isPartnerCustomer,
        companyQuestionsEnabled: isDirectCustomer && hasAIQuestionSignupEnabled,
      });

      if (newStep !== currentStep) {
        void navigateSignup({ route: newStep, updatedState: state });
      }
    }
  }, [
    application,
    applicationStatus,
    contextStatus,
    context,
    normalisedState,
    hasAIQuestionSignupEnabled,
  ]);
};
